import { Minus, Plus } from "lucide-react";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/PageHeader";
import { useContactAudit, useTagHistory } from "./hooks";

const sourceLabel: Record<string, string> = {
  admin: "admin screen",
  mcp: "Claude/Grok (MCP)",
  api: "API",
  site: "website",
  automation: "automation",
  form: "form",
  checkout: "checkout",
  billing: "billing",
  merge: "merge",
  email_report: "email report",
  keap_import: "Keap import",
  keap_mirror: "Keap sync",
  system: "system",
};

/** Every tag added or removed, with who, when and from where. */
export function TagHistorySheet({ id, open, onOpenChange }: { id: number; open: boolean; onOpenChange: (o: boolean) => void }) {
  const q = useTagHistory(id, open);
  const events = q.data?.events ?? [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent title="Tag history" description="Every tag added or removed, newest first. Times are Mountain Time.">
        {q.isLoading && <Skeleton className="m-4 h-40" />}
        {!q.isLoading && events.length === 0 && <Empty title="No tag changes recorded yet." />}
        <ol className="divide-y">
          {events.map((e) => (
            <li key={e.id} className="flex gap-3 px-4 py-2.5">
              {e.action === "applied" ? (
                <Plus className="mt-0.5 size-4 shrink-0 text-[var(--color-success)]" aria-label="added" />
              ) : (
                <Minus className="mt-0.5 size-4 shrink-0 text-[var(--color-destructive)]" aria-label="removed" />
              )}
              <div className="min-w-0 flex-1 text-sm">
                <div>
                  <span className="font-medium">{e.tag_name}</span> {e.action === "applied" ? "added" : "removed"}
                  <span className="num text-xs text-[var(--color-muted-foreground)]"> #{e.tag_id}</span>
                </div>
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  {formatDateTime(e.occurred_at)}, by {e.actor_label}, from the {sourceLabel[e.source] ?? e.source}
                  {e.source_ref ? ` (${e.source_ref})` : ""}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </SheetContent>
    </Sheet>
  );
}

const opLabel: Record<string, string> = {
  contacts_get: "Opened the full record",
  contacts_update: "Changed details",
  contacts_create: "Created the contact",
  contacts_merge: "Merged a duplicate",
  contacts_email_add: "Added an email address",
  contacts_email_remove: "Removed an email address",
  contacts_email_update: "Changed an email address",
  contacts_phone_add: "Added a phone number",
  contacts_phone_remove: "Removed a phone number",
  contacts_phone_update: "Changed a phone number",
  contacts_address_set: "Changed an address",
  tags_apply: "Added a tag",
  tags_remove: "Removed a tag",
  notes_add: "Added a note",
  notes_update: "Edited a note",
  notes_delete: "Deleted a note",
  files_download: "Downloaded a file",
  files_upload: "Attached a file",
  email_report_recipients: "Viewed in an email report list",
  contacts_match_reviews_list: "Viewed in the duplicates queue",
  contacts_match_review_decide: "Duplicate decision",
};

const viaLabel: Record<string, string> = {
  admin: "admin screen",
  rest: "API",
  mcp: "MCP (Claude/Grok)",
  replay: "outage replay",
  worker: "background worker",
  cli: "command line",
  webhook: "webhook",
  site: "website",
  test: "test",
};

/** Who opened or changed this record (from the audit log). */
export function AuditSheet({ id, open, onOpenChange }: { id: number; open: boolean; onOpenChange: (o: boolean) => void }) {
  const q = useContactAudit(id, open);
  const entries = q.data?.entries ?? [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent title="Who viewed or changed this" description="From the audit log, newest first (last 300 entries). Times are Mountain Time.">
        {q.isLoading && <Skeleton className="m-4 h-40" />}
        {!q.isLoading && entries.length === 0 && <Empty title="Nothing in the audit log for this contact yet." />}
        <ol className="divide-y">
          {entries.map((e) => (
            <li key={e.id} className="px-4 py-2.5 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{opLabel[e.op] ?? e.op.replace(/_/g, " ")}</span>
                {e.status !== "ok" && <Badge variant={e.status === "error" || e.status === "denied" ? "destructive" : "warning"}>{e.status.replace(/_/g, " ")}</Badge>}
              </div>
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {formatDateTime(e.occurred_at)}, {e.actor_label} through the {viaLabel[e.via] ?? e.via}
                {e.error ? `: ${e.error}` : ""}
              </div>
            </li>
          ))}
        </ol>
      </SheetContent>
    </Sheet>
  );
}
